import moment from "moment";
import { v4 as uuidv4 } from 'uuid';
import * as XLSX_STYLE from "xlsx-js-style";
import {
  ActionTypeEntity,
  AssignedModuleData,
  ModuleEntity,
  SelectedAccessoryEntity,
  UserInfoEntity,
  WaltzFeaturedCalculatedSolutionEntity,
} from "../interface";
import { FileDetails } from "../interface/file-details";
import { AdditionalPanelOnlyDetailEntity } from "../interface/office-additional-dealer-discount";
import { SolutionDetails, WaltzSolutionConfigurationCategoriesEntity, WaltzSolutionConfigurationGlassFinishesEntity, WaltzSolutionConfigurationGlassSubCategoryEntity, WaltzSolutionConfigurationGlassVarientsEntity } from "../interface/order";
import { SelectedPanelOnlyEntity, TreeStructureEnity } from "../interface/panel-only";
import { StorageEditedPricingVersionEntity } from "../interface/pricing-version";
import {
  arrowDownKeyCode,
  arrowLeftKeyCode,
  arrowRightKeyCode,
  arrowUpKeyCode,
  backSpaceKeyCode,
  copyKeyCode,
  ctrlKeyCode,
  cutKeyCode,
  deleteKeyCode,
  enterKeyCode,
  pasteKeyCode,
  selectAllKeyCode,
  tabSpacekeyCode,
  teamCategoryPermissionModuleNameMap,
  teamLists,
  undoKeyCode,
} from "../utils/constants";
import { DrawingDetailFolderNameEntity, SecondaryFactorsEnum, StorageKeyEnum } from "../utils/enum";
import {
  IsNullOrEmptyArray,
  IsNullOrUndefined,
  IsStringNullEmptyOrUndefined,
} from "../utils/null-check";
import { TWO_DECIMAL_PATTERN } from "./../utils/pattern";
import {
  getLocalStorageItem,
  getSessionStorageItem,
  setSessionStorageItem,
} from "./storage-service";

export const removeSpaceOnFirstCharacter = (event: any): void => {
  if (event.target.selectionStart === 0 && event.code === "Space") {
    event.preventDefault();
  }
};

export const checkValidInputOnKeyDown = (event: any, regExp: RegExp): void => {
  const key = event.which || event.keyCode;
  const isCtrlOrCmd = event.ctrlKey || event.metaKey;

  const isClipboardAction =
    isCtrlOrCmd &&
    (key === copyKeyCode || key === pasteKeyCode || key === cutKeyCode || key === selectAllKeyCode || key === ctrlKeyCode || key === undoKeyCode);

  const keyChecks =
    key &&
    key !== tabSpacekeyCode &&
    key !== backSpaceKeyCode &&
    key !== deleteKeyCode &&
    key !== enterKeyCode &&
    key !== arrowLeftKeyCode &&
    key !== arrowUpKeyCode &&
    key !== arrowRightKeyCode &&
    key !== arrowDownKeyCode &&
    !isClipboardAction;


  if (!regExp.test(event.key) && keyChecks) {
    event.preventDefault();
  }
};

export const handlePaste = (event: any, regex: RegExp): void => {
  const pastedText = event.clipboardData.getData('text');
  const textReplacePasted = pastedText.replace(regex, '');
  if (textReplacePasted !== pastedText) {
    event.preventDefault();
    const el = document.activeElement;
    if (el instanceof HTMLElement && el.isContentEditable) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(textReplacePasted));
      }
    }
    else if (el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement) {
      const { selectionStart, selectionEnd, value }: any = el;
      el.value = value.slice(0, selectionStart) + textReplacePasted + value.slice(selectionEnd);
      el.setSelectionRange(selectionStart + textReplacePasted.length, selectionStart + textReplacePasted.length);
    }
  }
};

export const capitalizeLetter = (str: string) => {
  return str.toUpperCase();
};

export const fileTypeFromName = (url: string): string => {

  let type: string = 'assets/images/office-icons/photo-icon.svg'

  if (!IsStringNullEmptyOrUndefined(url)) {
    const segments: string[] = url.split('/');
    const filename: string = segments.pop() || '';
    const extension: string = filename.split(".")?.pop() || ''

    if (!IsStringNullEmptyOrUndefined(extension)) {
      switch (extension) {

        case 'pdf':
          type = 'assets/images/office-icons/pdf-icon.svg'
          break;

        case 'ppt':
        case 'pptx':
          type = 'assets/images/office-icons/ppt-icon.svg'
          break;

        case 'docx':
        case 'doc':
          type = 'assets/images/office-icons/word-icon.svg'
          break;

        case 'xlsx':
        case 'xls':
          type = 'assets/images/office-icons/excel-icon.svg'
          break;

        case 'mp4':
        case 'm4v':
          type = 'assets/images/office-icons/video-icon.svg'
          break;

        default:
          break;
      }
    }
  }

  return type;
};

export const capitalizeFirstLetter = (input: string): string => {
  return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase();
};

export const validateFileExtension = (
  fileName: string,
  fileExtension: string[]
): boolean => {
  const extension: string | undefined = fileName.split(".").pop();
  if (extension) {
    return fileExtension.some((ext) => extension.includes(ext));
  }
  return false;
};

export const scrollTop = (): void => {
  document.body.scrollTop = 0; // For Safari
  document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
};

export const getBase64FromFile = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export const IsFormValid = (obj: object): boolean => {
  let count = 0;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Object.entries(obj).forEach(([key, value]) => {
    if (!IsStringNullEmptyOrUndefined(value)) {
      count += 1;
    }
  });

  return count === 0 ? true : false;
};

export const displayDateFormat = (dateStr: string): string => {
  return moment.utc(dateStr).format("DD/MM/YYYY");
};
export const displayDate = (dateStr: string): string => {
  return moment(dateStr).format("DD/MM/YYYY");
};

export const displayDateTimeFormat = (dateStr: string): string => {
  return moment.utc(dateStr).format("DD/MM/YYYY HH:mm A");
};

export const displayDateTimeFormatOnIST = (dateStr: string): string => {
  return moment.utc(dateStr).tz("Asia/Kolkata").format("DD/MM/YYYY HH:mm A");
};

export const displayProjectClosingBalance = (balance: number): string => {
  let formattedBalance = "";

  if (balance > 0) {
    formattedBalance = `${balance} DR`;
  } else if (balance < 0) {
    formattedBalance = `${Math.abs(balance)} CR`;
  } else {
    formattedBalance = '0 NIL';
  }

  return formattedBalance;
};

export const displayDateFormatForMetting = (dateStr: string): string => {
  return moment.utc(dateStr).format("DD/MM/YYYY");
};

export const checkActionAuthorization = (
  action: ActionTypeEntity,
  module: ModuleEntity
) => {
  let index: number = -1;
  const userStr: string | null = localStorage.getItem(
    StorageKeyEnum.JB_GLASS_USER_INFO
  );
  if (userStr && !IsStringNullEmptyOrUndefined(userStr)) {
    const userInfo: UserInfoEntity = JSON.parse(userStr);
    const modules: AssignedModuleData[] = userInfo.assignedModule || [];
    index = modules.findIndex(
      (e) => e.action === action && e.moduleName === module
    );
  }
  return index !== -1 ? true : false;
};

export const getAllowedTeamLists = (): { name: string; code: string }[] => {
  return teamLists.filter((team) =>
    checkActionAuthorization("upgrade", teamCategoryPermissionModuleNameMap[team.code])
  );
};

export const render30CharcterAfterDot = (rowData: string): string => {
  const name = rowData || "";

  const truncatedName = name.length > 30 ? name.substring(0, 30) + "..." : name;

  return truncatedName;
};

export const vehicleAutoSpaceValid = (str: string) => {
  const alphanumericIBAN = str.toUpperCase().replace(/[^a-zA-Z0-9]/g, "");
  const formattedIBAN = alphanumericIBAN.replace(
    /(\D{0,2})(\d{0,2})(\D{0,2})(\d{0,4})/,
    `$1${""} $2${""} $3${""} $4`
  );
  return formattedIBAN.trim();
};

export const reanderNumberOfCharcterAfterDot = (
  rowData: string,
  num: number
): string => {
  const name = rowData || "";

  const truncatedName =
    name.length > num ? name.substring(0, num) + "..." : name;

  return truncatedName;
};

function isValidDate(dateString: string): boolean {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) return false;

  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

export const generateExcel = (
  wsData: (string | number | null)[][],
  columns: string[],
  fileName: string,
  style: boolean
) => {
  const ws = XLSX_STYLE.utils.aoa_to_sheet(wsData);
  const ref = ws["!ref"];

  if (style) {
    const firstRowStyle = { font: { sz: 14, bold: true } };

    if (ref) {
      const range = XLSX_STYLE.utils.decode_range(ref);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = { c: C, r: 0 };
        const cellRef = XLSX_STYLE.utils.encode_cell(cellAddress);
        ws[cellRef].s = firstRowStyle;
      }
    }
  }

  const accountingStyleWithOutDecimalValue = {
    numFmt: "#,###,##0;-#,###,##0;",
  };
  const accountingStyleWithDecimalValue = {
    numFmt: "#,###,##0.###;-#,###,##0.###;",
  };

  for (const key in ws) {
    if (Object.prototype.hasOwnProperty.call(ws, key)) {
      let value = ws[key].v;
      if (isValidDate(value)) {
        ws[key].t = "d";
      }
    }
  }
  columns.forEach((columnName) => {
    const columnIndex = wsData[0].indexOf(columnName);
    if (columnIndex !== -1) {
      for (let rowIndex = 1; rowIndex < wsData.length; rowIndex++) {
        const cellRef = XLSX_STYLE.utils.encode_cell({
          r: rowIndex,
          c: columnIndex,
        });
        if (ws[cellRef]) {
          const cellValue = ws[cellRef].v;
          if (cellValue === 0) {
            ws[cellRef].s = accountingStyleWithOutDecimalValue;
          } else {
            const hasDecimal = Number.isInteger(cellValue);
            ws[cellRef].s = hasDecimal
              ? accountingStyleWithOutDecimalValue
              : accountingStyleWithDecimalValue;
          }
        }
      }
    }
  });

  const setColumnWidths = (
    ws: XLSX_STYLE.WorkSheet,
    wsData: (string | number | null)[][]
  ) => {
    const columnWidths: number[] = [];

    wsData.forEach((row) => {
      Object.keys(row).forEach((key: any, colIndex) => {
        const cellValue = row[key];
        if (cellValue !== undefined && cellValue !== null) {
          const cellWidth = cellValue.toString().length;
          columnWidths[colIndex] = Math.max(
            columnWidths[colIndex] || 0,
            cellWidth
          );
        }
      });
    });

    ws["!cols"] = columnWidths.map((width) => ({ wch: width + 5 }));
  };

  setColumnWidths(ws, wsData);

  const wb = XLSX_STYLE.utils.book_new();
  XLSX_STYLE.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX_STYLE.writeFile(wb, fileName);
};

export const storeWaltzSolutions = (
  orderId: string,
  solution: WaltzFeaturedCalculatedSolutionEntity[]
): void => {
  const str: string | null = getSessionStorageItem(
    `${StorageKeyEnum.WALTZ_ORDERS_SOLUTIONS}_${orderId}`
  );

  if (str && !IsStringNullEmptyOrUndefined(str)) {
    let parseData: WaltzFeaturedCalculatedSolutionEntity[] | [] =
      JSON.parse(str);

    if (parseData && !IsNullOrEmptyArray(parseData)) {
      for (const sol of solution) {
        const index = parseData.findIndex((e) => e.uuid === sol.uuid);

        if (index === -1) {
          (parseData as WaltzFeaturedCalculatedSolutionEntity[]).push(sol);
        } else {
          parseData[index] = sol;
        }
      }
    } else {
      parseData = solution;
    }
    setSessionStorageItem(
      `${StorageKeyEnum.WALTZ_ORDERS_SOLUTIONS}_${orderId}`,
      JSON.stringify(parseData)
    );
  } else {
    setSessionStorageItem(
      `${StorageKeyEnum.WALTZ_ORDERS_SOLUTIONS}_${orderId}`,
      JSON.stringify(solution)
    );
  }
};

export const fetchStoredWaltzSolutions = (
  orderId: string
): null | SolutionDetails[] | [] => {
  let parseData: SolutionDetails[] | [] = [];

  const str: string | null = getSessionStorageItem(
    `${StorageKeyEnum.WALTZ_ORDERS_SOLUTIONS}_${orderId}`
  );

  if (!str || IsNullOrUndefined(str)) {
    return null;
  }

  if (str && !IsStringNullEmptyOrUndefined(str)) {
    parseData = JSON.parse(str);
  }

  return parseData;
};

export const fetchStoredWaltzSingleSolution = (
  orderId: string,
  uuid: string
): null | SolutionDetails => {
  let solution: SolutionDetails | null = null;

  const str: string | null = getSessionStorageItem(
    `${StorageKeyEnum.WALTZ_ORDERS_SOLUTIONS}_${orderId}`
  );

  if (!str || IsNullOrUndefined(str)) {
    return null;
  }

  if (str && !IsStringNullEmptyOrUndefined(str)) {
    const parseData: SolutionDetails[] | [] = JSON.parse(str);
    const index = parseData.findIndex((e) => e.uuid === uuid);

    if (index !== -1) {
      solution = parseData[index];
    }

    return solution;
  }

  return solution;
};

export const fetchLastSolutionNo = (orderId: string): number => {
  let solutionNo: number = 0;

  const str: string | null = getSessionStorageItem(
    `${StorageKeyEnum.WALTZ_ORDERS_SOLUTIONS}_${orderId}`
  );

  if (!str || IsNullOrUndefined(str)) {
    return 0;
  }

  if (str && !IsStringNullEmptyOrUndefined(str)) {
    const parseData: SolutionDetails[] | [] = JSON.parse(str);
    solutionNo = IsNullOrEmptyArray(parseData)
      ? 0
      : parseData[parseData.length - 1].solutionNo;
  }

  return solutionNo;
};

export const downloadCsvFile = async (excelUrl: string, fileName: string) => {
  const response = await fetch(excelUrl);
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const fetchedEditedModulesData =
  (): null | StorageEditedPricingVersionEntity => {
    const str: string | null = getLocalStorageItem(
      StorageKeyEnum.EDITED_MODULES_FOR_PRICING_VERSION
    );

    let obj: null | StorageEditedPricingVersionEntity = null;

    if (str && !IsStringNullEmptyOrUndefined(str)) {
      obj = JSON.parse(str);
    }

    return obj;
  };

export const handleCancelClick = () => {
  const cancelButtons = document.querySelectorAll(".p-row-editor-cancel");
  cancelButtons.forEach((button) => {
    (button as HTMLElement).click();
  });
};

export const handleCheckNaN = (value: string) => {
  const isNaN =
    !TWO_DECIMAL_PATTERN.test(value) && value !== "-" && value !== "";
  return isNaN;
};

export const regionDisplayName = (secondaryFactor: string): string => {
  let displayName = "";

  if (secondaryFactor === SecondaryFactorsEnum.WITH_IN) {
    displayName = "Regional";
  }
  if (secondaryFactor === SecondaryFactorsEnum.CROSS) {
    displayName = "Cross";
  }
  if (secondaryFactor === SecondaryFactorsEnum.NA) {
    displayName = "Undefined";
  }
  if (secondaryFactor === SecondaryFactorsEnum.EXPORT) {
    displayName = "Export";
  }

  return displayName;
};

export const compareArray = (arr1: any, arr2: any): boolean => {
  if (arr1.length !== arr2.length) {
    return false;
  }

  for (let i = 0; i < arr1.length; i++) {
    let obj1 = arr1[i];
    let obj2 = arr2[i];
    if (JSON.stringify(obj1) !== JSON.stringify(obj2)) {
      return false;
    }
  }
  return true;
};

export const formatValue = (
  value: number,
  format: "CR" | "Lakh" | "K"
): any => {
  if (format === "CR") {
    return (value / 1e7).toFixed(2) + " CR";
  } else if (format === "K") {
    const intValue = Math.floor(value);
    return intValue >= 1000
      ? // `${Math.trunc(intValue / 1000)}k`
      `${(intValue / 1000).toFixed(2)}K`
      : `${(intValue / 1000).toFixed(2)}K`;
  } else {
    return value.toString();
  }
};

export const loggedInUserInfo = (): UserInfoEntity | null => {
  const str: string | null = getLocalStorageItem(
    StorageKeyEnum.JB_GLASS_USER_INFO
  );
  if (!str) {
    return null;
  }

  if (str && !IsStringNullEmptyOrUndefined(str)) {
    const userInfo: UserInfoEntity = JSON.parse(str);
    return userInfo;
  }
  return null;
};

export const checkActiveOrInActiveStructure = (id: string, treeStructure: TreeStructureEnity[], oldModels: string[] | [] = []): boolean => {
  for (const element of treeStructure) {
    if (element.id === id && !element.isActive && !oldModels.some((item) => item === element.id)) {
      return false;
    }
    if (element.children) {
      for (const subCategory of element.children) {
        if (subCategory.id === id && !subCategory.isActive && !oldModels.some((item) => item === subCategory.id)) {
          return false;
        }
        if (subCategory.children) {
          for (const finish of subCategory.children) {
            if (finish.id === id && !finish.isActive && !oldModels.some((item) => item === finish.id)) {
              return false;
            }
            if (finish.children) {
              for (const varinat of finish.children) {
                if (varinat.id === id && !varinat.isActive && !oldModels.some((item) => item === varinat.id)) {
                  return false;
                }
              }
            }
          }
        }
      }
    }
  }
  return true;
};

export const getStructureIds = (data: TreeStructureEnity[]): string[] => {
  let ids: string[] = [];

  for (const element of data) {
    ids.push(element.id)
    if (element.children) {
      for (const subCategory of element.children) {
        ids.push(subCategory.id)
        if (subCategory.children) {
          for (const finish of subCategory.children) {
            ids.push(finish.id)
            if (finish.children) {
              for (const variant of finish.children) {
                ids.push(variant.id);
              }
            }
          }
        }
      }
    }
  }

  return ids;
};

export const numberWithCommas = (x: string): string => {
  return x.toString().split(".")[0].length > 3
    ? x
      .toString()
      .substring(0, x.toString().split(".")[0].length - 3)
      .replace(/\B(?=(\d{2})+(?!\d))/g, ",") +
    "," +
    x.toString().substring(x.toString().split(".")[0].length - 3)
    : x.toString();
}

export const NothingArchitect = {
  id: "",
  architect: "Nothing from suggestion",
  name: "Nothing from suggestion",
  city: ""
};


export const convertStructureToAccessoriesData = (selectedAccessoriesStructure: TreeStructureEnity[] | []): SelectedAccessoryEntity[] | [] => {
  let flattenedData: SelectedAccessoryEntity[] | [] = [];
  selectedAccessoriesStructure.forEach((category) => {
    if (!category.children || category.children.length === 0) {
      (flattenedData as SelectedAccessoryEntity[]).push({
        uuid: uuidv4(),
        accCategoryName: category.label,
        accCategoryId: category.id,
        accSubCategoryName: '',
        accSubCategoryId: '',
        accFinishName: '',
        accFinishId: '',
        accVariantName: '',
        accVariantId: '',
        qtyType: 'default',
        defaultQtyValue: 1,
        rangeQtyFromValue: null,
        rangeQtyToValue: null,
        qtyDefaultError: '',
        qtyRangeError: ''
      });
      return;
    }

    category.children.forEach((subCategory) => {
      if (!subCategory.children || subCategory.children.length === 0) {
        (flattenedData as SelectedAccessoryEntity[]).push({
          uuid: uuidv4(),
          accCategoryName: category.label,
          accCategoryId: category.id,
          accSubCategoryName: subCategory.label,
          accSubCategoryId: subCategory.id,
          accFinishName: '',
          accFinishId: '',
          accVariantName: '',
          accVariantId: '',
          qtyType: 'default',
          defaultQtyValue: 1,
          rangeQtyFromValue: null,
          rangeQtyToValue: null,
          qtyDefaultError: '',
          qtyRangeError: ''
        });
        return;
      }

      subCategory.children.forEach((finish) => {
        if (!finish.children || finish.children.length === 0) {
          (flattenedData as SelectedAccessoryEntity[]).push({
            uuid: uuidv4(),
            accCategoryName: category.label,
            accCategoryId: category.id,
            accSubCategoryName: subCategory.label,
            accSubCategoryId: subCategory.id,
            accFinishName: finish.label,
            accFinishId: finish.id,
            accVariantName: '',
            accVariantId: '',
            qtyType: 'default',
            defaultQtyValue: 1,
            rangeQtyFromValue: null,
            rangeQtyToValue: null,
            qtyDefaultError: '',
            qtyRangeError: ''
          });
          return;
        }

        finish.children.forEach((variant) => {
          (flattenedData as SelectedAccessoryEntity[]).push({
            uuid: uuidv4(),
            accCategoryName: category.label,
            accCategoryId: category.id,
            accSubCategoryName: subCategory.label,
            accSubCategoryId: subCategory.id,
            accFinishName: finish.label,
            accFinishId: finish.id,
            accVariantName: variant.label,
            accVariantId: variant.id,
            qtyType: 'default',
            defaultQtyValue: 1,
            rangeQtyFromValue: null,
            rangeQtyToValue: null,
            qtyDefaultError: '',
            qtyRangeError: ''
          });
        });
      });
    });
  });
  return flattenedData;
}
export const fetchFileDetails = (file: File | null): FileDetails | null => {

  if (IsNullOrUndefined(file)) {
    return null
  }

  const fileName: string = (file as File).name;
  const fileSize: number = (file as File).size;
  // Extract the last extension
  const match = fileName.match(/\.([^.]+)$/);
  const fileType: string = match ? match[1].toLowerCase() : "";
  const displayFileSize: number = parseFloat((fileSize / (1024 * 1024)).toFixed(2));
  console.log({ fileName, fileSize, fileType, displayFileSize })
  return { fileName, fileSize, fileType, displayFileSize }

};

export const getFileFromURL = async (
  url: string,
  generateFileName: string
): Promise<File> => {
  const response = await fetch(url, {
    cache: "no-cache",
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch file: ${response.status} ${response.statusText}`
    );
  }

  const blob = await response.blob();

  if (blob.size === 0) {
    throw new Error(`Downloaded file is empty: ${url}`);
  }

  const urlPath = new URL(url).pathname;
  const originalFileName = urlPath.split("/").pop() || "";

  const extensionFromUrl = originalFileName.includes(".")
    ? originalFileName.split(".").pop()?.toLowerCase()
    : undefined;

  const mimeTypeMap: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    gif: "image/gif",
    bmp: "image/bmp",

    pdf: "application/pdf",

    mp4: "video/mp4",
    webm: "video/webm",
    mov: "video/quicktime",
    avi: "video/x-msvideo",
    mkv: "video/x-matroska",
    m4v: "video/x-m4v",

  };

  const serverMimeType =
    response.headers
      .get("content-type")
      ?.split(";")[0]
      .trim()
      .toLowerCase();

  const mimeType =
    serverMimeType &&
      serverMimeType !== "application/octet-stream"
      ? serverMimeType
      : blob.type &&
        blob.type !== "application/octet-stream"
        ? blob.type
        : mimeTypeMap[extensionFromUrl || ""] ||
        "application/octet-stream";

  const extensionMap: Record<string, string> = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/gif": "gif",
    "image/bmp": "bmp",

    "application/pdf": "pdf",

    "video/mp4": "mp4",
    "video/webm": "webm",
    "video/quicktime": "mov",
    "video/x-msvideo": "avi",
    "video/x-matroska": "mkv",
    "video/x-m4v": "m4v",
  };

  const extension =
    extensionFromUrl ||
    extensionMap[mimeType] ||
    "bin";

  const cleanFileName = generateFileName.replace(
    /\.[^/.]+$/,
    ""
  );

  const file = new File(
    [blob],
    `${cleanFileName}.${extension}`,
    {
      type: mimeType,
    }
  );

  if (file.size === 0) {
    throw new Error(
      `Generated file is empty: ${file.name}`
    );
  }

  return file;
};

export const getThumnailImgFor = (type: "photos" | "albums", url: string): string => {
  const folderToSplit = `/jb-library-${type}/`;
  if (IsStringNullEmptyOrUndefined(url)) {
    return "";
  }
  let arr: string[] = url.split(folderToSplit);
  arr = [
    ...arr.slice(0, 1),
    `/thumbnails${folderToSplit}`,
    ...arr.slice(1)
  ];
  return arr.join("");
};

export const startsWithSpace = (value: string): boolean => {
  return value.startsWith(" ");
};

export const endsWithSpace = (value: string): boolean => {
  return value.endsWith(" ");
};

export const getFormattedStatusName = (status: string): string => {
  return status
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export const handleDownloadFilesFromLinks = async (links: string[]): Promise<void> => {
  for (const url of links) {
    const filename = url.split('/').pop() ?? 'download';

    const link = document.createElement('a');
    link.href = url;
    link.target = "_blank";
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Wait 500ms between each download
    await new Promise(resolve => setTimeout(resolve, 500));
  }
};

export const fetchMatteValueFromFinishAndVariants = (
  categoryId: string, subCategoryId: string,
  finishId: string, variantId: string,
  glassCategories: WaltzSolutionConfigurationCategoriesEntity[]
): string => {
  let matteValue: string = "NO";

  console.log(" :: ", { categoryId, subCategoryId, finishId, variantId });

  const categoryIndex: number = glassCategories.findIndex(e => e._id === categoryId);

  if (categoryIndex !== -1) {
    const glassSubCategory: WaltzSolutionConfigurationGlassSubCategoryEntity[] | [] | null | undefined =
      glassCategories[categoryIndex].glassSubCategory;

    if (glassSubCategory && !IsNullOrUndefined(glassSubCategory) && !IsNullOrEmptyArray(glassSubCategory)) {

      const subCateogryIndex: number = glassSubCategory.findIndex(e => e._id === subCategoryId);

      if (subCateogryIndex !== -1) {
        const glassFinishes: WaltzSolutionConfigurationGlassFinishesEntity[] | [] | null | undefined =
          glassSubCategory[subCateogryIndex].glassFinishes;

        if (glassFinishes && !IsNullOrUndefined(glassFinishes) && !IsNullOrEmptyArray(glassFinishes)) {
          const finishIndex: number = glassFinishes.findIndex(e => e._id === finishId);

          if (finishIndex !== -1) {
            const glassVariants: WaltzSolutionConfigurationGlassVarientsEntity[] | [] | null | undefined =
              glassFinishes[finishIndex].glassVarients;

            if (glassVariants && IsNullOrEmptyArray(glassVariants)) {
              matteValue = glassFinishes[finishIndex].glassFinishMatte;
            } else {
              if (glassVariants && !IsNullOrEmptyArray(glassVariants)) {
                const variantIndex: number = glassVariants.findIndex(e => e._id === variantId);

                if (variantIndex !== -1) {
                  if (glassVariants[variantIndex] && !IsNullOrUndefined(glassVariants[variantIndex]) && glassVariants[variantIndex].glassVariantMatte) {
                    matteValue = glassVariants[variantIndex].glassVariantMatte;
                  }
                } else {
                  matteValue = glassFinishes[finishIndex].glassFinishMatte;
                }
              }
            }

          }
        }
      }
    }
  }

  return matteValue;
};

export function extractIdPanelStructure(data: TreeStructureEnity[]): AdditionalPanelOnlyDetailEntity[] {
  const results: AdditionalPanelOnlyDetailEntity[] = [];

  function traverse(node: TreeStructureEnity, path: Partial<AdditionalPanelOnlyDetailEntity> = {}) {
    switch (node.type) {
      case "category":
        path.categoryId = node.id;
        path.categoryName = node.label;
        break;
      case "subcategory":
        path.subCategoryId = node.id;
        path.subCategoryName = node.label;
        break;
      case "finish":
        path.finishId = node.id;
        path.finishName = node.label;
        if (IsNullOrEmptyArray(node.children as TreeStructureEnity[])) {
          results.push({ ...path, variantId: "", variantName: "" } as AdditionalPanelOnlyDetailEntity);
        }
        break;
      case "variant":
        path.variantId = node.id;
        path.variantName = node.label;
        results.push({ ...path } as AdditionalPanelOnlyDetailEntity);

        break;
    }

    node.children?.forEach(child => traverse(child, { ...path }));
  }

  data.forEach(root => traverse(root));
  return results;
};

export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};


type RecordType = {
  key: string;
  label: string;
  sqNo?: number,
  status?: string;
  PDFUrl?: string;
  childrenKey?: string;
  drawingId?: string;
  isAnnotationAdd?: boolean;
  createdAt?: string;
  children?: RecordType[];
};

export function mergeDrawingSetsRecords(
  arr1: RecordType[],
  arr2: RecordType[],
  uniqueBy: keyof RecordType = "key"
): RecordType[] {
  const merged = [...arr1, ...arr2];
  const seen = new Map<string, RecordType>();

  const specialKeys = new Set<string>([
    DrawingDetailFolderNameEntity.SD_APPROVED,
    DrawingDetailFolderNameEntity.SD_AWAITING,
    DrawingDetailFolderNameEntity.REVISION_FILE,
    DrawingDetailFolderNameEntity.PROCORE_FD,
    DrawingDetailFolderNameEntity.PROCORE_SD,
  ]);

  // Helper for special folders: dedupe by key + label
  const dedupeByKeyAndLabel = (list: RecordType[]): RecordType[] => {
    const map = new Map<string, RecordType>();
    for (const item of list) {
      const k = `${item.key}__${item.label}`;
      if (!map.has(k)) map.set(k, item);
    }
    return Array.from(map.values());
  };

  for (const item of merged) {
    const uniqueValue = item[uniqueBy];
    if (!uniqueValue) continue;

    const isSpecial = specialKeys.has(uniqueValue as string);

    if (!seen.has(uniqueValue as string)) {
      if (item.children) {
        if (isSpecial) {
          item.children = dedupeByKeyAndLabel(item.children);
        } else {
          item.children = mergeDrawingSetsRecords(item.children, [], uniqueBy);
        }
      }
      seen.set(uniqueValue as string, item);
    } else {
      const existing = seen.get(uniqueValue as string)!;

      if (item.children || existing.children) {
        if (isSpecial) {
          const mergedChildren = [
            ...(existing.children || []),
            ...(item.children || [])
          ];
          existing.children = dedupeByKeyAndLabel(mergedChildren);
        } else {
          existing.children = mergeDrawingSetsRecords(
            existing.children || [],
            item.children || [],
            uniqueBy
          );
        }
      }
    }
  }

  return Array.from(seen.values());
}
// utils/cleanHTML.js
export function cleanEmptyHTMLTags(html: any) {
  if (!html) return "";

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  function removeEmptyNodes(element: any) {
    const children = Array.from(element.children);
    children.forEach((child: any) => {
      removeEmptyNodes(child);

      if (!child.textContent.trim()) {
        child.remove();
      }
    });
  }

  removeEmptyNodes(tempDiv);
  return tempDiv.innerHTML.trim();
}




export function truncateString(str: string, maxLength: number) {
  if (!str) return "";
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + "...";
}

export const convertStructureToPanelOnlyData = (selectedPanelsStructure: TreeStructureEnity[] | []): SelectedPanelOnlyEntity[] | [] => {
  let flattenedData: SelectedPanelOnlyEntity[] | [] = [];
  selectedPanelsStructure.forEach((category) => {
    if (!category.children || category.children.length === 0) {
      (flattenedData as SelectedPanelOnlyEntity[]).push({
        uuid: uuidv4(),
        categoryName: category.label,
        categoryId: category.id,
        subCategoryName: '',
        subCategoryId: '',
        finishName: '',
        finishId: '',
        variantName: '',
        variantId: '',
        hsnCodeId: '',
      });
      return;
    }

    category.children.forEach((subCategory) => {
      if (!subCategory.children || subCategory.children.length === 0) {
        (flattenedData as SelectedPanelOnlyEntity[]).push({
          uuid: uuidv4(),
          categoryName: category.label,
          categoryId: category.id,
          subCategoryName: subCategory.label,
          subCategoryId: subCategory.id,
          finishName: '',
          finishId: '',
          variantName: '',
          variantId: '',
          hsnCodeId: '',
        });
        return;
      }

      subCategory.children.forEach((finish) => {
        if (!finish.children || finish.children.length === 0) {
          (flattenedData as SelectedPanelOnlyEntity[]).push({
            uuid: uuidv4(),
            categoryName: category.label,
            categoryId: category.id,
            subCategoryName: subCategory.label,
            subCategoryId: subCategory.id,
            finishName: finish.label,
            finishId: finish.id,
            variantName: '',
            variantId: '',
            hsnCodeId: '',
          });
          return;
        }

        finish.children.forEach((variant) => {
          (flattenedData as SelectedPanelOnlyEntity[]).push({
            uuid: uuidv4(),
            categoryName: category.label,
            categoryId: category.id,
            subCategoryName: subCategory.label,
            subCategoryId: subCategory.id,
            finishName: finish.label,
            finishId: finish.id,
            variantName: variant.label,
            variantId: variant.id,
            hsnCodeId: '',
          });
        });
      });
    });
  });
  return flattenedData;
}

export const handleCopyIrn = async (selectedIrn: string) => {
  if (!selectedIrn) return;

  try {
    await navigator.clipboard.writeText(selectedIrn);
  } catch {
    console.log("Copy failed");
  }
};

function getCSRefFromOrdernumber(orderNumber: string): string {
  let csRefNumber = '';
  const splitArr: string[] = orderNumber.split('/');

  if (splitArr.length === 1) return orderNumber;

  const arrayLength = splitArr.length;

  const lastElementOfArray = splitArr.pop();

  if (lastElementOfArray && lastElementOfArray.includes('V')) {
    const index = arrayLength - 2;
    csRefNumber = splitArr[index];
  } else {
    csRefNumber = lastElementOfArray ?? "";
  }

  return csRefNumber;
}

function getVersionOrdernumber(orderNumber: string): string {
  let versionNumber = '';
  const splitArr: string[] = orderNumber.split('/');

  if (splitArr.length === 1) return "";

  const arrayLength = splitArr.length;

  const lastElementOfArray = splitArr.pop();

  if (lastElementOfArray && lastElementOfArray.includes('V')) {
    versionNumber = lastElementOfArray;
  } else {
    versionNumber = '';
  }

  return versionNumber;
}


export const getOrderNumberWithVersion = (orderNumber: string): string => {
  let orderCSRefNumber = getCSRefFromOrdernumber(orderNumber);
  let orderVersionNumber = getVersionOrdernumber(orderNumber);
  return [orderCSRefNumber, orderVersionNumber].filter((item) => !IsStringNullEmptyOrUndefined(item)).join("/");
}
